import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import Sidebar from "../Dashboard/Sidebar";
import Topbar from "../Dashboard/Topbar";
import Loader from "../../components/Loader";
import { ToastContainer, toast } from "react-toastify";
import {
    paginateUsers,
    UserPaginationPayload,
    updateUserStatus,
} from "../../api/users";

const defaultPayload: UserPaginationPayload = {
    curPage: 1,
    perPage: 10,
    sortBy: "createdOn",
    direction: "desc",
    whereClause: [],
};

const Userlist: React.FC = () => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState<any[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [searchType, setSearchType] = useState<"name" | "email">("name");
    const [total, setTotal] = useState(0);
    const [payload, setPayload] =
        useState<UserPaginationPayload>(defaultPayload);
    const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
    const lastFetchKeyRef = useRef<string>("");

    const totalPages = Math.max(1, Math.ceil(total / payload.perPage));

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const filters: { key: string; value: string; operator: string }[] = [];

            if (searchTerm.trim()) {
                filters.push({
                    key: searchType,
                    value: searchTerm.trim(),
                    operator: "LIKE",
                });
            }

            const res = await paginateUsers({
                ...payload,
                whereClause: filters,
            });
            const data = res?.data?.data || res?.data || {};
            setUsers(
                Array.isArray(data.items) ? data.items : Array.isArray(data) ? data : []
            );
            setTotal(data.total || res.data.total || 0);
        } catch (err: any) {
            setUsers([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const key = `${payload.curPage}|${payload.perPage}|${payload.sortBy}|${payload.direction}|${searchTerm}|${searchType}`;
        if (lastFetchKeyRef.current === key) return;
        lastFetchKeyRef.current = key;
        fetchUsers();
    }, [payload.curPage, payload.perPage, payload.sortBy, payload.direction, searchTerm, searchType]);

    const goToPage = (p: number) => {
        setPayload((prev) => ({
            ...prev,
            curPage: Math.min(Math.max(1, p), totalPages),
        }));
    };

    const changePerPage = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = parseInt(e.target.value, 10) || 10;
        setPayload((prev) => ({ ...prev, perPage: value, curPage: 1 }));
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchTerm(value);
        setPayload((prev) => ({ ...prev, curPage: 1 }));
    };

    const handleSearchTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value as "name" | "email";
        setSearchType(value);
        setPayload((prev) => ({ ...prev, curPage: 1 }));
    };

    const handleToggleStatus = async (userId: string, currentStatus: boolean) => {
        setUpdatingStatus(userId);
        try {
            const res = await updateUserStatus({
                userId,
                isActive: !currentStatus,
            });
            
            // Show API response message in toast
            const message = res?.data?.message || `User ${!currentStatus ? "activated" : "deactivated"} successfully`;
            toast.success(message);
            
            // Update local state with API response data if available
            if (res?.data?.data) {
                setUsers((prevUsers) =>
                    prevUsers.map((user) =>
                        (user.id || user._id) === userId
                            ? { ...user, ...res.data.data }
                            : user
                    )
                );
            } else {
                // Fallback: update local state immediately
                setUsers((prevUsers) =>
                    prevUsers.map((user) =>
                        (user.id || user._id) === userId
                            ? { ...user, isActive: !currentStatus }
                            : user
                    )
                );
            }
        } catch (err: any) {
            toast.error(err?.response?.data?.message || "Failed to update user status");
        } finally {
            setUpdatingStatus(null);
        }
    };

    return (
        <div className="flex h-screen bg-gray-100">
            <ToastContainer position="top-right" autoClose={3000} />
            {loading && <Loader />}

            <Sidebar
                isOpen={isSidebarOpen}
                toggle={() => setIsSidebarOpen(!isSidebarOpen)}
            />

            <div className="flex-1 flex flex-col">
                <Topbar toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />

                <main className="p-4 md:p-4 overflow-y-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="w-full max-w-6xl mx-auto"
                    >
                        <motion.div
                            initial={{ scale: 0.98, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.4 }}
                            className="w-full rounded-3xl shadow-2xl bg-white p-6 md:p-8"
                        >
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                                    Users
                                </h2>
                            </div>

                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-600">
                                        Total: <span className="font-medium">{total}</span>
                                    </span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <div className="w-full md:w-48">
                                        <select
                                            value={searchType}
                                            onChange={handleSearchTypeChange}
                                            className="w-full mt-1 p-2 border rounded-lg"
                                        >
                                            <option value="name">Search by Name</option>
                                            <option value="email">Search by Email</option>
                                        </select>
                                    </div>
                                    <div className="w-full md:w-64">
                                        <input
                                            type="text"
                                            value={searchTerm}
                                            onChange={handleSearchChange}
                                            placeholder={searchType === "name" ? "Search by name..." : "Search by email..."}
                                            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                    <span className="text-sm text-gray-600">Rows:</span>
                                    <select
                                        className="border rounded-lg p-1 select-plain"
                                        value={payload.perPage}
                                        onChange={changePerPage}
                                    >
                                        <option value={5}>5</option>
                                        <option value={10}>10</option>
                                        <option value={20}>20</option>
                                        <option value={50}>50</option>
                                    </select>
                                </div>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                                                Name
                                            </th>
                                            <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                                                Email
                                            </th>
                                            <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                                                Status
                                            </th>
                                            <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                                                Change Status
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {users.length === 0 ? (
                                            <tr>
                                                <td
                                                    className="px-4 py-6 text-center text-red-500"
                                                    colSpan={5}
                                                >
                                                    No records found
                                                </td>
                                            </tr>
                                        ) : (
                                            users.map((user: any) => {
                                                const userId = user.id || user._id;
                                                const isUpdating = updatingStatus === userId;
                                                return (
                                                    <tr key={userId} className="border-t">

                                                        <td className="px-4 py-3 text-sm text-gray-800">
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-gray-100 border border-gray-200">
                                                                    <img
                                                                        src={
                                                                            user.profilePictureUrl ||
                                                                            user.profilePicture ||
                                                                            "/images/dummy-profile.webp"
                                                                        }
                                                                        alt={user.name || "User"}
                                                                        className="w-full h-full object-cover"
                                                                        onError={(e) => {
                                                                            (e.target as HTMLImageElement).src =
                                                                                "/images/dummy-profile.webp";
                                                                        }}
                                                                    />
                                                                </div>
                                                                <div className="flex flex-col">
                                                                    <span className="font-medium text-gray-900">
                                                                        {user.name || "—"}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-600">
                                                            {user.email || "—"}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm">
                                                            <span
                                                                className={`px-2 py-1 rounded-full text-xs font-medium ${user.isActive
                                                                    ? "bg-green-100 text-green-800"
                                                                    : "bg-red-100 text-red-800"
                                                                    }`}
                                                            >
                                                                {user.isActive ? "Active" : "Inactive"}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <label className="relative inline-flex items-center cursor-pointer">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={user.isActive || false}
                                                                    onChange={() =>
                                                                        handleToggleStatus(
                                                                            userId,
                                                                            user.isActive || false
                                                                        )
                                                                    }
                                                                    disabled={isUpdating}
                                                                    className="sr-only peer"
                                                                />
                                                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"></div>
                                                                {isUpdating && (
                                                                    <span className="ml-2 text-xs text-gray-500">
                                                                        Updating...
                                                                    </span>
                                                                )}
                                                            </label>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <hr className="my-4" />
                            <div className="flex items-center justify-between mt-6">
                                <div className="text-sm text-gray-600">
                                    Page {payload.curPage} of {totalPages}
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        onClick={() => goToPage(1)}
                                        disabled={payload.curPage === 1}
                                    >
                                        « First
                                    </button>
                                    <button
                                        className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        onClick={() => goToPage(payload.curPage - 1)}
                                        disabled={payload.curPage === 1}
                                    >
                                        ‹ Prev
                                    </button>
                                    <button
                                        className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        onClick={() => goToPage(payload.curPage + 1)}
                                        disabled={payload.curPage >= totalPages}
                                    >
                                        Next ›
                                    </button>
                                    <button
                                        className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        onClick={() => goToPage(totalPages)}
                                        disabled={payload.curPage >= totalPages}
                                    >
                                        Last »
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                </main>
            </div>
        </div>
    );
};

export default Userlist;
